import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SecretariatShell from './SecretariatShell';
import { SectionTitle } from './PageShell';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { CalendarDays, GraduationCap, Users, Clock, ArrowRight, Upload, FileDown, RefreshCw, Loader2, CheckCircle2, Save } from 'lucide-react';

const TYPE_LABEL = { conduite: 'Conduite', code: 'Code', examen: 'Examen', rvp: 'RVP', moto_plateau: '🏍 Moto · Plateau' };
const lessonColor = (l) => (l.type === 'moto_plateau' ? '#8b5cf6' : l.teacher_color);
const STATUS_BADGE = {
  effectue: ['Effectuée ✓', 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300'],
  annule: ['Annulée', 'bg-red-500/15 border-red-500/40 text-red-300'],
  absent_excuse: ['Abs. excusée', 'bg-orange-500/15 border-orange-500/40 text-orange-300'],
  absent_non_excuse: ['Abs. non excusée', 'bg-red-500/15 border-red-500/40 text-red-300'],
};

const QUICK_LINKS = [
  { label: 'Planning complet', to: '/secretariat/planning', icon: CalendarDays },
  { label: 'Fiches élèves', to: '/secretariat/eleves', icon: GraduationCap },
  { label: 'Import Codes Rousseau', to: '/secretariat/import', icon: Upload },
  { label: 'Export DSR', to: '/secretariat/dsr', icon: FileDown },
];

export default function SecretariatDashboardPage() {
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [todayLessons, setTodayLessons] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [backuping, setBackuping] = useState(false);
  const [backupMsg, setBackupMsg] = useState('');
  const [mailing, setMailing] = useState(false);

  const emailBackup = async () => {
    if (mailing) return;
    setMailing(true); setBackupMsg('');
    try {
      const r = await api.secBackupEmail();
      setBackupMsg(r.dry_run ? 'Mode test : email simulé (clé Resend absente) ✓' : `Sauvegarde envoyée par email à ${r.to} ✓`);
    } catch (e) {
      setBackupMsg(e?.response?.data?.detail || 'Erreur lors de l\u2019envoi — réessayez.');
    } finally { setMailing(false); }
  };

  const downloadBackup = async () => {
    if (backuping) return;
    setBackuping(true); setBackupMsg('');
    try {
      const res = await api.secBackup();
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sauvegarde-codevoiture-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setBackupMsg('Sauvegarde téléchargée ✓ — gardez ce fichier en lieu sûr.');
    } catch (_) {
      setBackupMsg('Erreur lors de la génération — réessayez.');
    } finally { setBackuping(false); }
  };

  const runSync = async () => {
    if (syncing) return;
    setSyncing(true); setSyncResult(null);
    try {
      const res = await api.secSyncData();
      setSyncResult({ ok: true, ...res });
      api.secStats().then(setStats).catch(() => {});
    } catch (e) {
      setSyncResult({ ok: false, error: e?.response?.data?.detail || 'Erreur inconnue' });
    } finally { setSyncing(false); }
  };

  useEffect(() => {
    if (authLoading || !user) return;
    api.secStats().then(setStats).catch(() => {});
    const today = new Date().toISOString().slice(0, 10);
    api.secLessons(today, today).then((d) => setTodayLessons(d.items || [])).catch(() => {});
  }, [authLoading, user]);

  if (authLoading) return <SecretariatShell><div className="p-12 text-white/50">Chargement…</div></SecretariatShell>;
  if (!user || (user.role !== 'secretaire' && !user.is_admin && !user.secretariat_access)) return <SecretariatShell><div className="p-12 text-center text-white/60">Accès réservé au secrétariat.</div></SecretariatShell>;

  const sorted = [...todayLessons].sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
  const first = sorted[0];
  const last = sorted[sorted.length - 1];

  const CARDS = [
    { label: 'Élèves actifs', value: stats?.eleves ?? '—', icon: GraduationCap, to: '/secretariat/eleves' },
    { label: 'Enseignants', value: stats?.enseignants ?? '—', icon: Users, to: '/secretariat/enseignants' },
    { label: "Leçons aujourd'hui", value: stats?.lecons_today ?? '—', icon: Clock, to: '/secretariat/planning' },
    { label: 'Leçons à venir', value: stats?.lecons_semaine ?? '—', icon: CalendarDays, to: '/secretariat/planning' },
  ];

  return (
    <SecretariatShell>
      <section className="max-w-6xl mx-auto px-4 lg:px-6 mt-8" data-testid="secretariat-dashboard">
        <SectionTitle kicker={`Bonjour ${user.name} · Secrétariat`} title="Ma journée" sub="Le déroulé du jour dans l'ordre : leçons et rendez-vous, puis la vue d'ensemble de l'auto-école." />

        {/* 1. Bandeau Aujourd'hui */}
        <div className="rounded-2xl border border-amber-500/40 bg-gradient-to-r from-amber-500/10 to-transparent p-4 sm:p-5 mb-5" data-testid="sec-today-summary">
          <div className="text-amber-400 text-[11px] font-bold uppercase tracking-[0.18em] font-mono-spec">Aujourd&apos;hui · {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
          <div className="mt-1.5 flex flex-wrap items-end gap-x-5 gap-y-1 text-white text-[13.5px]">
            <span><b className="font-serif-editorial text-[24px]">{sorted.length}</b> leçon{sorted.length > 1 ? 's' : ''} au planning</span>
            {first && <span className="text-white/70 pb-0.5">de <b className="text-white">{first.start_time}</b> à <b className="text-white">{last.start_time}</b></span>}
          </div>
        </div>

        {/* 2. Déroulé de la journée */}
        <div className="flex items-center justify-between gap-2 mb-3">
          <h2 className="font-serif-editorial text-white text-[20px] sm:text-[22px]">Déroulé de la journée</h2>
          <Link to="/secretariat/planning" className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full border border-white/15 text-white/80 hover:bg-white/5 text-[12px] font-semibold shrink-0" data-testid="sec-open-planning">
            Planning complet <ArrowRight size={12} />
          </Link>
        </div>
        {sorted.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-8 text-center text-white/45 text-[13px] mb-8" data-testid="sec-today-empty">
            Aucune leçon planifiée aujourd&apos;hui. <Link to="/secretariat/planning" className="text-amber-400 font-semibold">Ouvrir le planning →</Link>
          </div>
        ) : (
          <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] divide-y divide-white/5 mb-8" data-testid="sec-today-list">
            {sorted.map((l) => {
              const [lbl, cls] = STATUS_BADGE[l.status] || [];
              return (
                <div key={l.id} className="flex items-center gap-3 px-4 py-3">
                  <span className="w-1.5 h-9 rounded-full shrink-0" style={{ background: lessonColor(l) }} />
                  <span className="font-mono-spec text-white text-[14px] font-bold w-14 shrink-0">{l.start_time}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-[13.5px] font-semibold truncate">{l.student_name}</div>
                    <div className="text-white/50 text-[11.5px] truncate">{l.teacher_name} · {TYPE_LABEL[l.type] || l.type} · {l.duration_min} min</div>
                  </div>
                  {lbl && <span className={`shrink-0 px-2 py-0.5 rounded-md border text-[10.5px] font-bold ${cls}`}>{lbl}</span>}
                </div>
              );
            })}
          </div>
        )}

        {/* 3. Vue d'ensemble */}
        <h2 className="font-serif-editorial text-white text-[20px] sm:text-[22px] mb-3">Vue d&apos;ensemble</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
          {CARDS.map((c) => (
            <Link key={c.label} to={c.to} className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 hover:border-amber-500/40 transition" data-testid={`sec-card-${c.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
              <c.icon size={16} className="text-amber-400 mb-3" />
              <div className="font-serif-editorial text-white text-[32px] sm:text-[36px] leading-none">{c.value}</div>
              <div className="text-white/50 text-[12px] mt-1.5">{c.label}</div>
            </Link>
          ))}
        </div>

        {/* 4. Accès rapides */}
        <h2 className="font-serif-editorial text-white text-[20px] sm:text-[22px] mb-3">Accès rapides</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10">
          {QUICK_LINKS.map((q) => (
            <Link key={q.to} to={q.to} className="rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.07] p-4 flex items-center gap-2.5 transition" data-testid={`sec-quick-${q.to.split('/').pop()}`}>
              <q.icon size={15} className="text-amber-400 shrink-0" />
              <span className="text-white/85 text-[12.5px] font-semibold leading-tight">{q.label}</span>
            </Link>
          ))}
        </div>
        {/* 5. Sauvegarde complète */}
        <div className="rounded-2xl border border-emerald-700/40 bg-emerald-950/15 p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-5" data-testid="sec-backup-panel">
          <div className="w-11 h-11 rounded-xl bg-emerald-600/20 border border-emerald-700/40 flex items-center justify-center text-emerald-300 shrink-0">
            <Save size={18} />
          </div>
          <div className="flex-1">
            <div className="text-emerald-200 font-semibold text-[14.5px]">Sauvegarde complète (Excel)</div>
            <div className="text-white/60 text-[13px] mt-1">
              Télécharge un fichier Excel avec toutes les leçons (données et à venir), toutes les fiches de séance créées, la liste des élèves et des enseignants. <b className="text-white/80">Envoi automatique par email le 1ᵉʳ de chaque mois.</b>
            </div>
            {backupMsg && <div className={`mt-2 text-[13px] font-semibold ${backupMsg.includes('✓') ? 'text-emerald-300' : 'text-red-300'}`} data-testid="sec-backup-msg">{backupMsg}</div>}
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <button
              data-testid="sec-backup-btn"
              onClick={downloadBackup}
              disabled={backuping}
              className="h-10 px-4 rounded-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-emerald-950 text-[13px] font-bold inline-flex items-center justify-center gap-1.5"
            >
              {backuping ? (<><Loader2 size={13} className="animate-spin" /> Génération…</>) : (<><FileDown size={13} /> Télécharger la sauvegarde</>)}
            </button>
            <button
              data-testid="sec-backup-email-btn"
              onClick={emailBackup}
              disabled={mailing}
              className="h-10 px-4 rounded-full border border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-60 text-[13px] font-bold inline-flex items-center justify-center gap-1.5"
            >
              {mailing ? (<><Loader2 size={13} className="animate-spin" /> Envoi…</>) : '✉️ M\u2019envoyer par email'}
            </button>
          </div>
        </div>

        {/* 6. Reprise des données Preview → Production */}
        <div className="rounded-2xl border border-sky-700/40 bg-sky-950/15 p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-10" data-testid="sec-sync-panel">
          <div className="w-11 h-11 rounded-xl bg-sky-600/20 border border-sky-700/40 flex items-center justify-center text-sky-300 shrink-0">
            <RefreshCw size={18} className={syncing ? 'animate-spin' : ''} />
          </div>
          <div className="flex-1">
            <div className="text-sky-200 font-semibold text-[14.5px]">Récupérer les élèves &amp; le planning (jusqu&apos;à septembre)</div>
            <div className="text-white/60 text-[13px] mt-1">
              Recopie en 1 clic les 109 fiches élèves et les leçons août→septembre (import Codes Rousseau) dans la base de cet environnement. À utiliser après un déploiement. Sans risque : peut être relancé plusieurs fois.
            </div>
            {syncResult && syncResult.ok && (
              <div className="mt-3 inline-flex items-center gap-2 text-emerald-300 text-[13px] font-semibold" data-testid="sec-sync-ok">
                <CheckCircle2 size={14} />
                Terminé · {syncResult.sec_students?.created ?? 0} élèves créés ({syncResult.sec_students?.updated ?? 0} à jour) · {syncResult.sec_lessons?.created ?? 0} leçons créées ({syncResult.sec_lessons?.updated ?? 0} à jour)
                {syncResult.fusion && (syncResult.fusion.teachers_merged + syncResult.fusion.students_merged + syncResult.fusion.lessons_merged) > 0 && (
                  <span className="text-sky-300">· {syncResult.fusion.teachers_merged + syncResult.fusion.students_merged} doublon(s) fusionné(s)</span>
                )}
              </div>
            )}
            {syncResult && !syncResult.ok && (
              <div className="mt-3 text-red-300 text-[13px] font-semibold">Erreur : {syncResult.error}</div>
            )}
          </div>
          <button
            data-testid="sec-sync-btn"
            onClick={runSync}
            disabled={syncing}
            className="h-10 px-4 rounded-full bg-sky-500 hover:bg-sky-400 disabled:opacity-60 text-sky-950 text-[13px] font-bold inline-flex items-center gap-1.5 shrink-0"
          >
            {syncing ? (<><Loader2 size={13} className="animate-spin" /> Import…</>) : (<><RefreshCw size={13} /> Importer les données</>)}
          </button>
        </div>
      </section>
    </SecretariatShell>
  );
}
